import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import scrollToTheTop from "@lib/scrollToTheTop";

import { ORDER_MESSAGE_ZONE } from "../../../constants";
import useOrderFormik from "../../../hooks/useOrderFormik";
import saveAddressBookEntry from "../services/saveAddress";
import type { TNewAddressForm } from "../types";
import mapAddressValuesToOrder from "../utils/mapAddressValuesToOrder";
import mapOrderDataToNewAddressData from "../utils/mapOrderDataToNewAddressData";
import validateAddressForm from "../utils/validateAddressForm";

type TAddressFormReturn = {
  isLoading: boolean;
  handleFormSubmit: () => Promise<boolean>;
};

const useCreateAddress = (isLockerForm: boolean): TAddressFormReturn => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const { values, setValues, setTouched, setErrors } = useOrderFormik();

  const setMessage = useMessage();

  const showAddressSaveErrorMessage = useCallback(
    (message?: string) => {
      setMessage({
        message: message || t("order-message-failed_to_save_new_recipient"),
        props: {
          variant: "error",
          icon: <Icon icon="warning" size="1.45rem" />,
          canClose: true,
          padding: "0.45rem 0.75rem",
        },
        zone: ORDER_MESSAGE_ZONE,
      });
      scrollToTheTop();
    },
    [setMessage, t]
  );

  // As this is form inside another form, we have to manually handle validation and data submit
  const handleFormSubmit = useCallback(async () => {
    setIsLoading(true);
    // Extracting only address data from whole order data object
    const addressData = mapOrderDataToNewAddressData(values);

    // Need to touch fields manually to show validation messages
    const touched = {};
    Object.keys(addressData).forEach((fieldName) => {
      touched[fieldName] = true;
    });
    setTouched(touched);

    const validationResult = await validateAddressForm<TNewAddressForm>(addressData, true);

    if (!validationResult.isSuccess) {
      setIsLoading(false);
      showAddressSaveErrorMessage();
      if (validationResult.errorDetails) {
        setErrors(validationResult.errorDetails);
      }
      return false;
    }

    const result = await saveAddressBookEntry(addressData, !!isLockerForm);

    setIsLoading(false);

    if (!result.isSuccess) {
      showAddressSaveErrorMessage(result.errorMessage);
      if (result.errorDetails) {
        setErrors(result.errorDetails);
      }
      return false;
    }

    const addressId = result.data?.id;

    // Address saved successfully, setting newly created address values to order
    const orderFieldsFromAddress = mapAddressValuesToOrder(addressData as TNewAddressForm, addressId);
    setValues((oldValues) => ({ ...oldValues, ...orderFieldsFromAddress }));
    return true;
  }, [isLockerForm, setErrors, setTouched, setValues, showAddressSaveErrorMessage, values]);

  return { isLoading, handleFormSubmit };
};
export default useCreateAddress;
